import React, { Component } from "react";
import { Card, CardBody, Row, Col, Table, Badge } from "reactstrap";
import AiAdminLayout from "./layout";
import { fetchApi } from "./api";

// Pricing per 1M tokens (approximate)
const PRICING = {
  gemini: { input: 0.075, output: 0.30 },    // Gemini 2.5 Flash
  openai: { input: 0.15, output: 0.60 },      // GPT-4o-mini
};

function estimateCost(inputTokens, outputTokens, provider = "gemini") {
  const p = PRICING[provider] || PRICING.gemini;
  const inputCost = (inputTokens / 1_000_000) * p.input;
  const outputCost = (outputTokens / 1_000_000) * p.output;
  return { inputCost, outputCost, total: inputCost + outputCost };
}

function formatUSD(val) {
  if (val < 0.01) return "<$0.01";
  return "$" + val.toFixed(2);
}

class AiAdminDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      overview: null,
      usage: null,
      intents: [],
      tools: [],
      feedback: [],
      shops: [],
      loading: true,
    };
  }

  async componentDidMount() {
    try {
      const [overview, usage, intents, tools, feedback, shops] = await Promise.all([
        fetchApi("/stats/overview"),
        fetchApi("/stats/usage-over-time?period=30d"),
        fetchApi("/stats/intents"),
        fetchApi("/stats/tools"),
        fetchApi("/stats/feedback"),
        fetchApi("/stats/shops"),
      ]);
      this.setState({ overview, usage, intents, tools, feedback, shops, loading: false });
    } catch (err) {
      console.error("Dashboard load error:", err);
      this.setState({ loading: false });
    }
  }

  renderStatCard(label, value, icon, color) {
    return (
      <Col md={3} sm={6} className="mb-3">
        <Card style={{ borderLeft: `4px solid ${color}`, borderRadius: 8 }}>
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase" }}>
                  {label}
                </div>
                <h3 className="mb-0 font-weight-bold">{value}</h3>
              </div>
              <i className={icon} style={{ fontSize: 28, color, opacity: 0.6 }} />
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  renderIntentChart() {
    const { intents } = this.state;
    if (!intents.length) return null;

    const total = intents.reduce((s, i) => s + parseInt(i.count), 0);
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe", "#43e97b",
      "#fa709a", "#fee140", "#a18cd1", "#fbc2eb", "#ff9a9e",
    ];

    return (
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <CardBody>
          <h6 className="font-weight-bold mb-3">Intent Distribution</h6>
          {intents.map((item, i) => {
            const pct = total > 0 ? ((parseInt(item.count) / total) * 100).toFixed(1) : 0;
            return (
              <div key={i} className="mb-2">
                <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                  <span>{item.intent || "Unknown"}</span>
                  <span className="text-muted">{item.count} ({pct}%)</span>
                </div>
                <div
                  style={{
                    height: 6,
                    backgroundColor: "#f0f0f0",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      backgroundColor: colors[i % colors.length],
                      borderRadius: 3,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </CardBody>
      </Card>
    );
  }

  renderToolChart() {
    const { tools } = this.state;
    if (!tools.length) return null;

    const maxCount = Math.max(...tools.map((t) => parseInt(t.count)));

    return (
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <CardBody>
          <h6 className="font-weight-bold mb-3">Tool Usage</h6>
          {tools.slice(0, 10).map((item, i) => (
            <div key={i} className="mb-2">
              <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                <span style={{ fontFamily: "monospace" }}>{item.tool_name}</span>
                <span className="text-muted">{item.count}</span>
              </div>
              <div
                style={{
                  height: 6,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(parseInt(item.count) / maxCount) * 100}%`,
                    height: "100%",
                    backgroundColor: "#667eea",
                    borderRadius: 3,
                  }}
                />
              </div>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  }

  renderUsageChart() {
    const { usage } = this.state;
    if (!usage || !usage.messages_per_day?.length) return null;

    const data = usage.messages_per_day;
    const maxVal = Math.max(...data.map((d) => parseInt(d.count)), 1);

    return (
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <CardBody>
          <h6 className="font-weight-bold mb-3">Messages (Last 30 Days)</h6>
          <div className="d-flex align-items-end" style={{ height: 150, gap: 2 }}>
            {data.map((d, i) => {
              const h = (parseInt(d.count) / maxVal) * 130;
              return (
                <div
                  key={i}
                  title={`${d.date}: ${d.count} messages`}
                  style={{
                    flex: 1,
                    height: Math.max(h, 2),
                    backgroundColor: "#667eea",
                    borderRadius: "2px 2px 0 0",
                    opacity: 0.8,
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </div>
          <div className="d-flex justify-content-between mt-1" style={{ fontSize: 10, color: "#999" }}>
            <span>{data[0]?.date}</span>
            <span>{data[data.length - 1]?.date}</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  renderFeedbackChart() {
    const { feedback } = this.state;
    if (!feedback.length) return null;

    const labels = { 1: "Bad", 2: "Poor", 3: "OK", 4: "Good", 5: "Excellent" };
    const colors = { 1: "#ef4444", 2: "#f97316", 3: "#eab308", 4: "#22c55e", 5: "#3b82f6" };
    const total = feedback.reduce((s, f) => s + parseInt(f.count), 0);

    return (
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <CardBody>
          <h6 className="font-weight-bold mb-3">Feedback Ratings</h6>
          <div className="d-flex align-items-end justify-content-center" style={{ height: 100, gap: 8 }}>
            {[1, 2, 3, 4, 5].map((rating) => {
              const item = feedback.find((f) => parseInt(f.rating) === rating);
              const count = item ? parseInt(item.count) : 0;
              const h = total > 0 ? (count / total) * 80 : 0;
              return (
                <div key={rating} className="text-center">
                  <div
                    style={{
                      width: 36,
                      height: Math.max(h, 4),
                      backgroundColor: colors[rating],
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                  <div style={{ fontSize: 11, marginTop: 4 }}>{labels[rating]}</div>
                  <div style={{ fontSize: 10, color: "#999" }}>{count}</div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    );
  }

  renderShopsTable() {
    const { shops } = this.state;
    if (!shops.length) return null;

    return (
      <Card style={{ borderRadius: 8 }}>
        <CardBody>
          <h6 className="font-weight-bold mb-3">Shop AI Usage</h6>
          <Table responsive size="sm" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Shop ID</th>
                <th>Conversations</th>
                <th>Messages</th>
                <th>Knowledge Docs</th>
                <th>AI Status</th>
                <th>Provider</th>
              </tr>
            </thead>
            <tbody>
              {shops.map((s, i) => (
                <tr key={i}>
                  <td className="font-weight-bold">{s.shop_id}</td>
                  <td>{s.conversation_count}</td>
                  <td>{s.message_count}</td>
                  <td>{s.knowledge?.doc_count || 0}</td>
                  <td>
                    <Badge color={s.config?.is_enabled ? "success" : "secondary"} pill>
                      {s.config?.is_enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </td>
                  <td>{s.config?.provider || "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    );
  }

  render() {
    const { overview, loading } = this.state;

    if (loading) {
      return (
        <AiAdminLayout>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="mt-2 text-muted">Loading dashboard...</p>
          </div>
        </AiAdminLayout>
      );
    }

    return (
      <AiAdminLayout>
        <h4 className="font-weight-bold mb-4">Dashboard</h4>

        {/* Stat Cards */}
        <Row>
          {this.renderStatCard(
            "Total Conversations",
            overview?.total_conversations || 0,
            "simple-icon-bubbles",
            "#667eea",
          )}
          {this.renderStatCard(
            "Total Messages",
            overview?.total_messages || 0,
            "simple-icon-speech",
            "#764ba2",
          )}
          {this.renderStatCard(
            "Avg Latency",
            `${overview?.avg_latency_ms || 0}ms`,
            "simple-icon-speedometer",
            "#43e97b",
          )}
          {this.renderStatCard(
            "Avg Rating",
            `${overview?.avg_rating || "—"}/5`,
            "simple-icon-star",
            "#f093fb",
          )}
        </Row>

        <Row>
          {this.renderStatCard(
            "Knowledge Documents",
            overview?.total_knowledge_documents || 0,
            "iconsminds-library",
            "#4facfe",
          )}
          {this.renderStatCard(
            "Knowledge Chunks",
            overview?.total_knowledge_chunks || 0,
            "simple-icon-docs",
            "#fa709a",
          )}
          {this.renderStatCard(
            "AI Configs",
            `${overview?.enabled_configs || 0}/${overview?.total_configs || 0}`,
            "simple-icon-settings",
            "#fee140",
          )}
          {this.renderStatCard(
            "Audit Logs",
            overview?.total_audit_logs || 0,
            "simple-icon-list",
            "#a18cd1",
          )}
        </Row>

        {(() => {
          const inputTokens = overview?.total_input_tokens || 0;
          const outputTokens = overview?.total_output_tokens || 0;
          const cost = estimateCost(inputTokens, outputTokens);
          return (
            <>
              <Row>
                {this.renderStatCard(
                  "Input Tokens",
                  inputTokens.toLocaleString(),
                  "simple-icon-login",
                  "#22c55e",
                )}
                {this.renderStatCard(
                  "Output Tokens",
                  outputTokens.toLocaleString(),
                  "simple-icon-logout",
                  "#3b82f6",
                )}
                {this.renderStatCard(
                  "Total Tokens",
                  (overview?.total_tokens || 0).toLocaleString(),
                  "simple-icon-energy",
                  "#ef4444",
                )}
                {this.renderStatCard(
                  "Est. Cost",
                  formatUSD(cost.total),
                  "simple-icon-wallet",
                  "#f59e0b",
                )}
              </Row>
              {cost.total > 0 && (
                <div className="mb-3 text-muted" style={{ fontSize: 11, marginTop: -8 }}>
                  Input: {formatUSD(cost.inputCost)} &middot; Output: {formatUSD(cost.outputCost)}
                  &nbsp;(Gemini 2.5 Flash: $0.075/1M input, $0.30/1M output)
                </div>
              )}
            </>
          );
        })()}

        {/* Charts */}
        <Row>
          <Col lg={8}>{this.renderUsageChart()}</Col>
          <Col lg={4}>{this.renderFeedbackChart()}</Col>
        </Row>

        <Row>
          <Col lg={6}>{this.renderIntentChart()}</Col>
          <Col lg={6}>{this.renderToolChart()}</Col>
        </Row>

        {/* Shops Table */}
        {this.renderShopsTable()}
      </AiAdminLayout>
    );
  }
}

export default AiAdminDashboard;
